import { AppLayout } from "@/layouts/AppLayout/AppLayout";
import { AuthLayout } from "@/layouts/AuthLayout/AuthLayout";
import { RootLayout } from "@/layouts/RootLayout";
import { Outlet, RouteObject, redirect } from "react-router";

import { RouteErrorBoundary } from "@/components/ErrorBoundary/RouteErrorBoundary";
import { FeaturePermission } from "@/constants/Permission.constant";
import {
  appIndexRedirect,
  redirectIfAuthenticated,
  requireAuth,
  requireRoles,
  rootRedirect
} from "@/loaders/auth.loaders";

// Auth components (direct imports to avoid barrel pollution)
import { ROUTE_BROWSE_WAYPOINTS_INTERACTIVE } from "@/constants/Route.constant";
import { ChangeLogContainer } from "@/containers/ChangeLog";
import { ForgotPasswordContainer } from "@/containers/ForgotPassword";
import { LoginEntry } from "@/containers/Login/LoginEntry";
import { RecoveryContainer } from "@/containers/Recovery";
import { RecoveryExpiredContainer } from "@/containers/RecoveryExpired";
import { DashboardProvider } from "@/Providers/DashboardProvider";
import { FilterProvider } from "@/Providers/FilterProvider";
import { HEADER_SLOT } from "@/constants/Route.constant";

export const routes: RouteObject[] = [
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        index: true,
        loader: rootRedirect
      },
      {
        path: "auth",
        loader: redirectIfAuthenticated,
        errorElement: <RouteErrorBoundary />,
        element: <AuthLayout />,
        children: [
          {
            index: true,
            loader: () => {
              throw redirect("/auth/login");
            }
          },
          {
            path: "login",
            element: <LoginEntry />,
            handle: {
              title: "Login",
              header: { hidden: true }
            }
          },
          {
            path: "forgot",
            element: <ForgotPasswordContainer />,
            handle: {
              title: "Login",
              header: { hidden: true }
            }
          },
          {
            path: "recovery/expired",
            element: <RecoveryExpiredContainer />,
            handle: {
              title: "Login",
              header: { hidden: true }
            }
          },
          {
            path: "recovery/:token",
            element: <RecoveryContainer />,
            handle: {
              title: "Login",
              header: { hidden: true }
            }
          },
          {
            path: "confirm",
            element: <></>
          }
        ]
      },
      {
        path: "app",
        element: (
          <AppLayout>
            <Outlet />
          </AppLayout>
        ),
        loader: requireAuth,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            loader: appIndexRedirect
          },
          {
            path: "home",
            lazy: async () => {
              const { HomePage } =
                await import("@/features/home/pages/Home.page");
              return {
                Component: HomePage,
                handle: {
                  title: "Home",
                  header: { titleKey: "HOME" }
                }
              };
            }
          },
          {
            path: "dashboard/:id?",
            lazy: async () => {
              const DashboardContainer = (
                await import("@/containers/Dashboard/DashboardContainer")
              ).default;
              const DashboardWithProvider = () => (
                <DashboardProvider>
                  <DashboardContainer />
                </DashboardProvider>
              );
              return {
                Component: DashboardWithProvider,
                handle: {
                  title: "Dashboard",
                  header: { titleKey: "DASHBOARD" }
                }
              };
            }
          },
          {
            path: "user-management",
            lazy: async () => {
              const { UserManagement } =
                await import("@/features/user-management/layouts/UserManagement");
              return {
                Component: UserManagement,
                loader: requireRoles(FeaturePermission.ADMIN),
                handle: {
                  title: "User management",
                  header: { titleKey: "USERS" }
                }
              };
            },
            children: [
              {
                path: "view-users",
                lazy: async () => {
                  const ViewUsers = (
                    await import("@/features/user-management/pages/ViewUsers/ViewUsers")
                  ).default;
                  return {
                    Component: ViewUsers,
                    handle: {
                      title: "View users",
                      header: { titleKey: "USERS" }
                    }
                  };
                }
              },
              {
                path: "invite-user",
                lazy: async () => {
                  const { InviteUserPage } =
                    await import("@/features/invitations/pages/invite-user.page");
                  return {
                    Component: InviteUserPage,
                    handle: {
                      title: "Invite user",
                      header: { titleKey: "USERS" }
                    }
                  };
                }
              },
              {
                path: "view-companies",
                lazy: async () => {
                  const ViewCompanies = (
                    await import("@/features/user-management/pages/ViewCompanies/ViewCompanies")
                  ).default;
                  return {
                    Component: ViewCompanies,
                    handle: {
                      title: "View companies",
                      header: { titleKey: "COMPANIES" }
                    }
                  };
                }
              },
              {
                path: "view-partners",
                lazy: async () => {
                  const ViewPartners = (
                    await import("@/features/user-management/pages/ViewPartners/ViewPartners")
                  ).default;
                  return {
                    Component: ViewPartners,
                    handle: {
                      title: "View partners",
                      header: { titleKey: "USERS" }
                    }
                  };
                }
              },
              {
                path: "view-projects",
                lazy: async () => {
                  const { ViewProjects } =
                    await import("@/features/user-management/pages/ViewProjects.page");
                  return {
                    Component: ViewProjects,
                    handle: {
                      title: "View projects",
                      header: { titleKey: "USERS" }
                    }
                  };
                }
              },
              {
                path: "view-project-archives",
                lazy: async () => {
                  const { ViewProjectArchives } =
                    await import("@/features/user-management/pages/ViewProjectArchives.page");
                  return {
                    Component: ViewProjectArchives,
                    handle: { title: "View project archives" }
                  };
                }
              },
              {
                path: "add-user",
                loader: () => {
                  throw redirect("/app/user-management/view-users");
                }
              },
              {
                path: "add-company",
                lazy: async () => {
                  const AddCompany = (
                    await import("@/features/user-management/pages/AddCompany/AddCompany")
                  ).default;
                  return {
                    Component: AddCompany,
                    handle: { title: "Add company" }
                  };
                }
              },
              {
                path: "add-partner",
                lazy: async () => {
                  const AddPartner = (
                    await import("@/features/user-management/pages/AddPartner/AddPartner")
                  ).default;
                  return {
                    Component: AddPartner,
                    handle: { title: "Add partner" }
                  };
                }
              },
              {
                path: "project/new",
                lazy: async () => {
                  const { ProjectCreate } =
                    await import("@/features/projects/pages/ProjectCreate.page");
                  return {
                    Component: ProjectCreate,
                    handle: { title: "Add project" }
                  };
                }
              },
              {
                path: "project/:companyId/:projectId",
                lazy: async () => {
                  const { ProjectUpdate } =
                    await import("@/features/projects/pages/ProjectUpdate.page");
                  return {
                    Component: ProjectUpdate,
                    handle: { title: "Update project" }
                  };
                }
              },
              {
                path: "gis-admin",
                lazy: async () => {
                  const { GISAdmin } =
                    await import("@/features/user-management/pages/GISAdmin/GISAdmin");
                  return {
                    Component: GISAdmin,
                    loader: requireRoles(FeaturePermission.ADMIN),
                    handle: {
                      title: "GIS Admin",
                      header: { titleKey: "GIS_ADMIN" }
                    }
                  };
                }
              }
            ]
          },
          {
            path: "gis-upload",
            lazy: async () => {
              const { GISUploadContainer } =
                await import("@/containers/GISUpload/GISUploadContainer");
              return {
                Component: GISUploadContainer,
                loader: requireRoles(FeaturePermission.ADMIN),
                handle: {
                  title: "GIS Upload",
                  header: { titleKey: "GIS_UPLOAD" }
                }
              };
            },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { GISNewUploadNew } =
                    await import("@/containers/GISUpload/GISUploadNew");
                  return {
                    Component: GISNewUploadNew,
                    handle: { title: "New GIS Upload" }
                  };
                }
              },
              {
                path: ":uploadId",
                lazy: async () => {
                  const { GISUploadExisting } =
                    await import("@/containers/GISUpload/GISUploadExisting");
                  return {
                    Component: GISUploadExisting,
                    handle: { title: "Existing GIS Upload" }
                  };
                }
              }
            ]
          },
          {
            path: "project-status/:id?",
            lazy: async () => {
              const ProjectStatusContainer = (
                await import("@/containers/ProjectStatus/ProjectStatusContainer")
              ).default;
              const ProjectStatusWithProvider = () => (
                <FilterProvider>
                  <ProjectStatusContainer />
                </FilterProvider>
              );
              return {
                Component: ProjectStatusWithProvider,
                handle: {
                  title: "Project status",
                  header: {
                    titleKey: ({ search, t }) => {
                      const projectName = search.get("project-name");
                      return projectName
                        ? `${t("PROJECT_STATUS")} - ${projectName}`
                        : t("PROJECT_STATUS");
                    },
                    extraSlots: [HEADER_SLOT.PROJECT_ACTIONS]
                  }
                }
              };
            }
          },
          {
            path: "logs",
            element: <ChangeLogContainer />,
            loader: requireRoles(FeaturePermission.LOGS),
            handle: {
              title: "Activities",
              header: { titleKey: "LOGS" }
            }
          },
          {
            path: "waypoint/:id?",
            lazy: async () => {
              const WaypointContainer = (
                await import("@/containers/Waypoint/WaypointContainer")
              ).default;
              const WaypointWithProvider = () => (
                <FilterProvider>
                  <WaypointContainer />
                </FilterProvider>
              );
              return {
                Component: WaypointWithProvider,
                handle: {
                  title: "Waypoint",
                  header: {
                    titleKey: "WAYPOINT",
                    extraSlots: [HEADER_SLOT.WAYPOINT_ACTIONS]
                  }
                }
              };
            }
          },
          {
            path: "job",
            lazy: async () => {
              const { JobContainer } =
                await import("@/containers/JobContainer/JobContainer");
              return {
                Component: JobContainer,
                handle: {
                  title: "Job",
                  header: { titleKey: "JOB_DETAILS" }
                }
              };
            }
          },
          {
            path: "session/:id?",
            lazy: async () => {
              const SessionContainer = (
                await import("@/containers/Session/SessionContainer")
              ).default;
              return {
                Component: SessionContainer,
                handle: {
                  title: "Session",
                  header: { titleKey: "SESSION" }
                }
              };
            }
          },
          {
            path: "top-sound",
            lazy: async () => {
              const { TopSoundDisplay } =
                await import("@/features/topSounding/pages/TopSoundDisplay.page");
              return {
                Component: TopSoundDisplay,
                handle: {
                  title: "Top sound",
                  header: { titleKey: "TOP_SOUND" }
                }
              };
            }
          },
          {
            path: "correlation/:id?",
            lazy: async () => {
              const CorrelationContainer = (
                await import("@/containers/Correlation/CorrelationContainer")
              ).default;
              return {
                Component: CorrelationContainer,
                handle: {
                  title: "Correlation",
                  header: { titleKey: "CORRELATION" }
                }
              };
            }
          },
          {
            path: "consumption-profile/:id?",
            lazy: async () => {
              const ConsumptionProfileContainer = (
                await import("@/containers/ConsumptionProfile/ConsumptionProfileContainer")
              ).default;
              return {
                Component: ConsumptionProfileContainer,
                handle: {
                  title: "Consumption profile",
                  header: { titleKey: "CONSUMPTION_PROFILE" }
                }
              };
            }
          },
          {
            path: "profile",
            lazy: async () => {
              const { ProfileContainer } = await import("@/containers/Profile");
              return {
                Component: ProfileContainer,
                handle: {
                  title: "Profile",
                  header: { titleKey: "PROFILE" }
                }
              };
            }
          },
          {
            path: "sensor-management",
            lazy: async () => {
              const StockManagementContainer = (
                await import("@/containers/StockManagement/StockManagementContainer")
              ).default;
              const SensorManagement = () => (
                <StockManagementContainer isSensorManagement={true} />
              );
              return {
                Component: SensorManagement,
                loader: requireRoles(FeaturePermission.STOCK_MANAGEMENT),
                handle: {
                  title: "Sensor management",
                  header: { titleKey: "SENSOR_MANAGEMENT" }
                }
              };
            }
          },
          {
            path: "relay-management",
            lazy: async () => {
              const StockManagementContainer = (
                await import("@/containers/StockManagement/StockManagementContainer")
              ).default;
              const RelayManagement = () => (
                <StockManagementContainer isSensorManagement={false} />
              );
              return {
                Component: RelayManagement,
                loader: requireRoles(FeaturePermission.STOCK_MANAGEMENT),
                handle: {
                  title: "Relay management",
                  header: { titleKey: "RELAY_MANAGEMENT" }
                }
              };
            }
          },
          {
            path: "browse-classic",
            lazy: async () => {
              const BrowseContainer = (
                await import("@/containers/Browse/BrowseContainer")
              ).default;
              return {
                Component: BrowseContainer,
                handle: {
                  title: "Browse",
                  header: {
                    titleKey: "BROWSE"
                  }
                }
              };
            },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { BrowseEmpty } =
                    await import("@/features/browse/pages/BrowseEmpty.old.page");
                  return {
                    Component: BrowseEmpty,
                    handle: { title: "Browse" }
                  };
                }
              },
              {
                path: "waypoints",
                lazy: async () => {
                  const WaypointsListView = (
                    await import("@/containers/Browse/Waypoints/ListView")
                  ).default;
                  return {
                    Component: WaypointsListView,
                    handle: { title: "Waypoints" }
                  };
                }
              },
              {
                path: "sessions",
                lazy: async () => {
                  const SessionListView = (
                    await import("@/containers/Browse/Sessions/ListView")
                  ).default;
                  return {
                    Component: SessionListView,
                    handle: { title: "Sessions" }
                  };
                }
              },
              {
                path: "jobs",
                lazy: async () => {
                  const JobsListView = (
                    await import("@/containers/Browse/Jobs/ListView")
                  ).default;
                  return {
                    Component: JobsListView,
                    handle: { title: "Jobs" }
                  };
                }
              },
              {
                path: "waypoint-logs",
                lazy: async () => {
                  const WaypointLogsListView = (
                    await import("@/containers/Browse/WaypointLogs/ListView")
                  ).default;
                  return {
                    Component: WaypointLogsListView,
                    handle: { title: "Waypoint logs" }
                  };
                }
              },
              {
                path: "relays",
                lazy: async () => {
                  const RelaysListView = (
                    await import("@/containers/Browse/Relays/ListView")
                  ).default;
                  return {
                    Component: RelaysListView,
                    handle: { title: "Relays" }
                  };
                }
              },
              {
                path: "consumption-profile",
                lazy: async () => {
                  const ConsumptionProfileListView = (
                    await import("@/containers/Browse/ConsumptionProfile/ListView")
                  ).default;
                  return {
                    Component: ConsumptionProfileListView,
                    handle: { title: "Consumption profiling" }
                  };
                }
              }
            ]
          },
          {
            path: "browse",
            element: <Outlet />,
            handle: {
              title: "Browse Interactive",
              header: {
                extraSlots: [HEADER_SLOT.BROWSE_VIEW_SWITCH]
              }
            },
            children: [
              {
                index: true,
                loader: () => {
                  throw redirect("/app/browse/interactive");
                }
              },
              {
                path: "interactive",
                lazy: async () => {
                  const { BrowseInteractiveLayout } =
                    await import("@/features/browse/layouts/BrowseInteractive.layout");
                  return {
                    Component: BrowseInteractiveLayout,
                    handle: {
                      title: "Browse",
                      header: {
                        titleKey: "BROWSE"
                      }
                    }
                  };
                },
                children: [
                  {
                    index: true,
                    loader: () => {
                      throw redirect(ROUTE_BROWSE_WAYPOINTS_INTERACTIVE);
                    }
                  },
                  {
                    path: "jobs",
                    element: null,
                    handle: { title: "Jobs" }
                  },
                  {
                    path: "relays",
                    element: null,
                    handle: { title: "Relays" }
                  },
                  {
                    path: "sensors",
                    element: null,
                    handle: { title: "Sensors" }
                  },
                  {
                    path: "sessions",
                    element: null,
                    handle: { title: "Sessions" }
                  },
                  {
                    path: "waypoints",
                    element: null,
                    handle: { title: "Waypoints" }
                  },
                  {
                    path: "waypoint-logs",
                    element: null,
                    handle: { title: "Waypoint Logs" }
                  }
                ]
              },
              {
                path: "list",
                handle: {
                  title: "Browse List",
                  header: {
                    titleKey: "BROWSE"
                  }
                },
                lazy: async () => {
                  const { BrowseListLayout } =
                    await import("@/features/browse/layouts/BrowseList.layout");
                  return {
                    Component: BrowseListLayout,
                    handle: { title: "Browse List" }
                  };
                },
                children: [
                  {
                    index: true,
                    loader: () => {
                      throw redirect("/app/browse/list/waypoints");
                    }
                  },
                  {
                    path: "jobs",
                    element: null,
                    handle: { title: "Jobs" }
                  },
                  {
                    path: "relays",
                    element: null,
                    handle: { title: "Relays" }
                  },
                  {
                    path: "sensors",
                    element: null,
                    handle: { title: "Sensors" }
                  },
                  {
                    path: "sessions",
                    element: null,
                    handle: { title: "Sessions" }
                  },
                  {
                    path: "waypoints",
                    element: null,
                    handle: { title: "Waypoints" }
                  },
                  {
                    path: "waypoint-logs",
                    element: null,
                    handle: { title: "Waypoint Logs" }
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: "accept-invitation",
        loader: redirectIfAuthenticated,
        lazy: async () => {
          const { AcceptInvitationPage } =
            await import("@/features/invitations/pages/accept-invitation.page");
          return { Component: AcceptInvitationPage };
        },
        handle: {
          title: "Accept invitation",
          header: { hidden: true }
        }
      },
      {
        path: "accept-invitation-success",
        loader: redirectIfAuthenticated,
        lazy: async () => {
          const { AcceptInvitationSuccessPage } =
            await import("@/features/invitations/pages/accept-invitation-success.page");
          return { Component: AcceptInvitationSuccessPage };
        },
        handle: {
          title: "Invitation accepted",
          header: { hidden: true }
        }
      }
    ]
  },
  {
    path: "*",
    lazy: async () => {
      const { NotFound } = await import("@/components/NotFound/NotFound");
      return {
        Component: NotFound,
        handle: {
          title: "Page Not Found",
          header: { hidden: true }
        }
      };
    }
  }
];
